// AllProjects.jsx
import React, { useState, useEffect } from "react";
import { toast } from "react-toastify";
import { Link, useNavigate } from "react-router-dom";
import AdminNavbar from "../Layout/AdminNavbar";
import API_BASE_URL from "../config";

/* ─────────────────────────────────────────────
   Confirmation Modal Component
   ───────────────────────────────────────────── */
const ConfirmModal = ({ isOpen, onClose, onConfirm, title, message, confirmLabel, confirmVariant, icon }) => {
    useEffect(() => {
        if (!isOpen) return;
        const handleKey = (e) => { if (e.key === "Escape") onClose(); };
        document.addEventListener("keydown", handleKey);
        return () => document.removeEventListener("keydown", handleKey);
    }, [isOpen, onClose]);

    if (!isOpen) return null;

    const variantStyles = {
        danger: {
            btn: { background: "#dc3545", color: "#fff", border: "none" },
            iconBg: "#fff0f0",
            iconColor: "#dc3545",
        },
        warning: {
            btn: { background: "#f59e0b", color: "#fff", border: "none" },
            iconBg: "#fffbeb",
            iconColor: "#d97706",
        },
    };
    const v = variantStyles[confirmVariant] || variantStyles.danger;

    return (
        <>
            {/* Backdrop */}
            <div
                onClick={onClose}
                style={{
                    position: "fixed", inset: 0, background: "rgba(0,0,0,0.45)",
                    zIndex: 1050, backdropFilter: "blur(2px)",
                    animation: "fadeIn 0.15s ease",
                }}
            />
            {/* Modal */}
            <div
                role="dialog"
                aria-modal="true"
                aria-labelledby="confirm-modal-title"
                style={{
                    position: "fixed", top: "50%", left: "50%",
                    transform: "translate(-50%, -50%)",
                    zIndex: 1060, width: "min(440px, 90vw)",
                    background: "#fff",
                    borderRadius: 12,
                    boxShadow: "0 20px 60px rgba(0,0,0,0.2), 0 4px 16px rgba(0,0,0,0.1)",
                    animation: "slideUp 0.2s ease",
                    overflow: "hidden",
                }}
            >
                {/* Top accent bar */}
                <div style={{
                    height: 4,
                    background: confirmVariant === "warning"
                        ? "linear-gradient(90deg, #f59e0b, #fbbf24)"
                        : "linear-gradient(90deg, #dc3545, #e85d6b)",
                }} />

                <div style={{ padding: "28px 28px 24px" }}>
                    {/* Icon + Title */}
                    <div style={{ display: "flex", alignItems: "flex-start", gap: 16, marginBottom: 16 }}>
                        <div style={{
                            width: 44, height: 44, borderRadius: 10,
                            background: v.iconBg,
                            display: "flex", alignItems: "center", justifyContent: "center",
                            flexShrink: 0, fontSize: 22, color: v.iconColor,
                        }}>
                            {icon}
                        </div>
                        <div>
                            <h5 id="confirm-modal-title" style={{
                                margin: 0, fontWeight: 700, fontSize: 17,
                                color: "#111827", lineHeight: 1.3,
                            }}>
                                {title}
                            </h5>
                            <p style={{
                                margin: "6px 0 0", fontSize: 14,
                                color: "#6b7280", lineHeight: 1.6,
                            }}>
                                {message}
                            </p>
                        </div>
                    </div>

                    {/* Buttons */}
                    <div style={{
                        display: "flex", justifyContent: "flex-end",
                        gap: 10, marginTop: 24,
                    }}>
                        <button
                            onClick={onConfirm}
                            style={{
                                padding: "8px 20px", borderRadius: 7, cursor: "pointer",
                                fontSize: 14, fontWeight: 600,
                                transition: "opacity 0.15s",
                                ...v.btn,
                            }}
                            onMouseEnter={e => e.currentTarget.style.opacity = "0.88"}
                            onMouseLeave={e => e.currentTarget.style.opacity = "1"}
                        >
                            {confirmLabel}
                        </button>
                        <button
                            onClick={onClose}
                            style={{
                                padding: "8px 20px", borderRadius: 7, cursor: "pointer",
                                background: "#f3f4f6", color: "#374151",
                                border: "1px solid #e5e7eb",
                                fontSize: 14, fontWeight: 500,
                                transition: "background 0.15s",
                            }}
                            onMouseEnter={e => e.currentTarget.style.background = "#e5e7eb"}
                            onMouseLeave={e => e.currentTarget.style.background = "#f3f4f6"}
                        >
                            Cancel
                        </button>
                        
                    </div>
                </div>
            </div>

            {/* Keyframe styles injected once */}
            <style>{`
                @keyframes fadeIn { from { opacity:0 } to { opacity:1 } }
                @keyframes slideUp { from { opacity:0; transform:translate(-50%,-46%) } to { opacity:1; transform:translate(-50%,-50%) } }
            `}</style>
        </>
    );
};

/* ─────────────────────────────────────────────
   Main Component
   ───────────────────────────────────────────── */
const AllProjects = () => {
    const [projects, setProjects] = useState([]);
    const [filteredProjects, setFilteredProjects] = useState([]);
    const [error, setError] = useState("");

    // Filters
    const [search, setSearch] = useState("");
    const [statusFilter, setStatusFilter] = useState("");
    const [typeFilter, setTypeFilter] = useState("");
    const [priorityFilter, setPriorityFilter] = useState("");
    const [showFilters, setShowFilters] = useState(false);

    // Sorting
    const [sortConfig, setSortConfig] = useState({ key: null, direction: "asc" });

    // Download state
    const [downloading, setDownloading] = useState(false);

    // ── Modal state ──────────────────────────────
    const [modal, setModal] = useState({
        isOpen: false,
        type: null,       // "delete" | "archive"
        project: null,
    });

    const openDeleteModal = (project) =>
        setModal({ isOpen: true, type: "delete", project });

    const openArchiveModal = (project) =>
        setModal({ isOpen: true, type: "archive", project });

    const closeModal = () =>
        setModal({ isOpen: false, type: null, project: null });

    const handleModalConfirm = () => {
        if (modal.type === "delete") confirmDelete(modal.project);
        else if (modal.type === "archive") confirmArchive(modal.project);
        closeModal();
    };
    // ─────────────────────────────────────────────

    const navigate = useNavigate();

    const STATUSES = ["Planning", "Active", "On Hold", "Completed", "Archived", "Cancelled"];
    const PRIORITIES = ["Low", "Medium", "High", "Critical"];
    const PROJECT_TYPES = ["Internal", "Client", "Research", "Maintenance", "Compliance", "Other"];

    // Bootstrap CSS loader
    useEffect(() => {
        const bootstrapLink = document.createElement("link");
        bootstrapLink.rel = "stylesheet";
        bootstrapLink.href = "https://cdn.jsdelivr.net/npm/bootstrap@5.3.2/dist/css/bootstrap.min.css";
        document.head.appendChild(bootstrapLink);
        const iconsLink = document.createElement("link");
        iconsLink.rel = "stylesheet";
        iconsLink.href = "https://cdn.jsdelivr.net/npm/bootstrap-icons@1.11.1/font/bootstrap-icons.css";
        document.head.appendChild(iconsLink);
        return () => { bootstrapLink.remove(); iconsLink.remove(); };
    }, []);

    // Auth check
    useEffect(() => {
        const token = localStorage.getItem("token");
        const roles = JSON.parse(localStorage.getItem("roles") || "[]");
        if (!token) { toast.error("You must be logged in to access this page."); navigate("/"); return; }
        if (!roles.includes("ROLE_ADMIN")) { toast.error("You are not authorized to access this page."); navigate("/"); return; }
    }, [navigate]);

    // Fetch projects
    useEffect(() => {
        const token = localStorage.getItem("token");
        fetch(`${API_BASE_URL}/api/projects`, {
            headers: token ? { Authorization: `Bearer ${token}` } : {},
        })
            .then((res) => { if (!res.ok) throw new Error("Failed to fetch projects"); return res.json(); })
            .then((data) => {
                const list = Array.isArray(data) ? data : data.content || [];
                setProjects(list);
                setFilteredProjects(list);
            })
            .catch((err) => setError(err.message));
    }, []);

    // Apply filters and sorting
    useEffect(() => {
        let result = [...projects];
        if (search) {
            result = result.filter(
                (p) =>
                    p.name?.toLowerCase().includes(search.toLowerCase()) ||
                    p.key?.toLowerCase().includes(search.toLowerCase()) ||
                    p.referenceNumber?.toLowerCase().includes(search.toLowerCase())
            );
        }
        if (statusFilter) result = result.filter((p) => p.status === statusFilter);
        if (typeFilter) result = result.filter((p) => p.projectType === typeFilter);
        if (priorityFilter) result = result.filter((p) => p.priority === priorityFilter);
        if (sortConfig.key) {
            result.sort((a, b) => {
                const valA = a[sortConfig.key] ?? "";
                const valB = b[sortConfig.key] ?? "";
                if (valA < valB) return sortConfig.direction === "asc" ? -1 : 1;
                if (valA > valB) return sortConfig.direction === "asc" ? 1 : -1;
                return 0;
            });
        }
        setFilteredProjects(result);
    }, [search, statusFilter, typeFilter, priorityFilter, projects, sortConfig]);

    const handleSort = (key) => {
        setSortConfig((prev) => ({
            key,
            direction: prev.key === key && prev.direction === "asc" ? "desc" : "asc",
        }));
    };
    const getSortArrow = (key) => {
        if (sortConfig.key !== key) return "";
        return sortConfig.direction === "asc" ? "↑" : "↓";
    };

    // ── Actual delete / archive logic (no window.confirm) ──
    const confirmDelete = async (project) => {
        try {
            const token = localStorage.getItem("token");
            const res = await fetch(`${API_BASE_URL}/api/projects/${project.id}`, {
                method: "DELETE",
                headers: { Authorization: `Bearer ${token}` },
            });
            if (!res.ok) throw new Error("Failed to delete project.");
            toast.success(`Project "${project.name}" deleted.`);
            setProjects((prev) => prev.filter((p) => p.id !== project.id));
        } catch (err) {
            toast.error(err.message || "Cannot delete — project may be linked to active tasks.");
        }
    };

    const confirmArchive = async (project) => {
        try {
            const token = localStorage.getItem("token");
            const res = await fetch(`${API_BASE_URL}/api/projects/${project.id}`, {
                method: "PUT",
                headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
                body: JSON.stringify({ ...project, status: "Archived" }),
            });
            if (!res.ok) throw new Error("Failed to archive project.");
            toast.success(`Project "${project.name}" archived.`);
            setProjects((prev) =>
                prev.map((p) => p.id === project.id ? { ...p, status: "Archived" } : p)
            );
        } catch (err) {
            toast.error(err.message || "Failed to archive.");
        }
    };
    // ──────────────────────────────────────────────────────

    const downloadReport = async (format = "xlsx") => {
        try {
            setDownloading(true);
            const token = localStorage.getItem("token");
            const res = await fetch(`${API_BASE_URL}/api/reports/projects?format=${format}`, {
                headers: token ? { Authorization: `Bearer ${token}` } : {},
            });
            if (!res.ok) throw new Error(`Failed to download ${format} report`);
            const blob = await res.blob();
            const url = URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            a.download = `Projects_Report.${format}`;
            document.body.appendChild(a);
            a.click();
            a.remove();
            URL.revokeObjectURL(url);
            toast.success("Downloaded successfully");
        } catch (err) {
            toast.error(err.message || "Failed to download report");
        } finally {
            setDownloading(false);
        }
    };

    const formatDate = (iso) =>
        iso ? new Date(iso).toLocaleDateString("en-GB") : "";

    const getStatusBadge = (status) => {
        const map = {
            Active: "success", Planning: "primary", "On Hold": "warning",
            Completed: "info", Archived: "secondary", Cancelled: "danger",
        };
        return <span className={`badge bg-${map[status] || "secondary"}`}>{status}</span>;
    };

    const getPriorityBadge = (priority) => {
        const map = { Low: "success", Medium: "warning", High: "danger", Critical: "dark" };
        return <span className={`badge bg-${map[priority] || "secondary"}`}>{priority}</span>;
    };

    // Modal config derived from state
    const modalConfig = modal.type === "delete"
        ? {
            title: "Delete Project",
            message: (
                <>
                    Are you sure you want to permanently delete{" "}
                    <strong>"{modal.project?.name}"</strong>?{" "}
                    This action <strong>cannot be undone</strong> and may affect linked tasks.
                </>
            ),
            confirmLabel: "Delete Project",
            confirmVariant: "danger",
            icon: "🗑️",
        }
        : {
            title: "Archive Project",
            message: (
                <>
                    Archive <strong>"{modal.project?.name}"</strong>?{" "}
                    The project will be moved to Archived status and hidden from active views.
                    You can restore it later if needed.
                </>
            ),
            confirmLabel: "Archive Project",
            confirmVariant: "warning",
            icon: "📦",
        };

    return (
        <>
            <div className="fixed-top">
                <AdminNavbar />
            </div>
            <div className="container-fluid my-4" style={{ paddingTop: "60px" }}></div>
            <div className="d-flex flex-column min-vh-100">
                <div className="container-fluid p-3 bg-light flex-grow-1">

                    {/* Page Header */}
                    <div className="d-flex justify-content-between align-items-center mb-3">
                        <h4 className="fw-bold">
                            <i className="bi bi-kanban me-2"></i> Projects
                        </h4>
                        <div>
                            <button className="btn btn-primary btn-sm" onClick={() => navigate("/addproject")}>
                                New Project
                            </button>
                        </div>
                    </div>

                    {/* Collapsible Filters */}
                    <div
                        className="p-2 bg-light border mb-2"
                        style={{ cursor: "pointer" }}
                        onClick={() => setShowFilters(!showFilters)}
                    >
                        <strong>{showFilters ? "− Filters" : "+ Filters"}</strong>
                    </div>
                    {showFilters && (
                        <div className="row mb-3">
                            <div className="col-md-4 mb-2">
                                <input
                                    type="text"
                                    className="form-control"
                                    placeholder="Search by name, key, or reference…"
                                    value={search}
                                    onChange={(e) => setSearch(e.target.value)}
                                />
                            </div>
                            <div className="col-md-2 mb-2">
                                <select className="form-select" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
                                    <option value="">All Statuses</option>
                                    {STATUSES.map((s) => <option key={s}>{s}</option>)}
                                </select>
                            </div>
                            <div className="col-md-2 mb-2">
                                <select className="form-select" value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)}>
                                    <option value="">All Types</option>
                                    {PROJECT_TYPES.map((t) => <option key={t}>{t}</option>)}
                                </select>
                            </div>
                            <div className="col-md-2 mb-2">
                                <select className="form-select" value={priorityFilter} onChange={(e) => setPriorityFilter(e.target.value)}>
                                    <option value="">All Priorities</option>
                                    {PRIORITIES.map((p) => <option key={p}>{p}</option>)}
                                </select>
                            </div>
                        </div>
                    )}

                    {/* Toolbar */}
                    <div className="d-flex justify-content-between align-items-center p-2 bg-light border mb-2">
                        <div className="d-flex align-items-center gap-2">
                            <button
                                className="btn btn-sm btn-outline-secondary"
                                onClick={() => downloadReport("xlsx")}
                                disabled={downloading}
                                title="Download Excel report"
                            >
                                {downloading ? "Downloading..." : "📊"}
                            </button>
                        </div>
                        <div className="d-flex align-items-center">
                            <span>Total: {filteredProjects.length}</span>
                        </div>
                    </div>

                    {/* Table */}
                    <div style={{ overflowX: "auto" }}>
                        <table className="table table-sm align-middle text-start">
                            <thead>
                                <tr>
                                    <th>View | Edit</th>
                                    <th onClick={() => handleSort("key")} style={{ cursor: "pointer" }}>Key {getSortArrow("key")}</th>
                                    <th onClick={() => handleSort("name")} style={{ cursor: "pointer" }}>Project Name {getSortArrow("name")}</th>
                                    <th>Type</th>
                                    <th onClick={() => handleSort("status")} style={{ cursor: "pointer" }}>Status {getSortArrow("status")}</th>
                                    <th>Priority</th>
                                    <th>Visibility</th>
                                    <th>Customer</th>
                                    <th onClick={() => handleSort("startDate")} style={{ cursor: "pointer" }}>Start {getSortArrow("startDate")}</th>
                                    <th onClick={() => handleSort("endDate")} style={{ cursor: "pointer" }}>End {getSortArrow("endDate")}</th>
                                    <th>Budget</th>
                                    <th onClick={() => handleSort("createdAt")} style={{ cursor: "pointer" }}>Created {getSortArrow("createdAt")}</th>
                                    <th>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {filteredProjects.length > 0 ? (
                                    filteredProjects.map((p) => (
                                        <tr key={p.id}>
                                            <td>
                                                <Link to={`/viewproject/${p.id}`} className="me-2 text-primary">View</Link>
                                                |
                                                <Link to={`/editproject/${p.id}`} className="ms-2 text-primary">Edit</Link>
                                            </td>
                                            <td>
                                                <span className="badge" style={{ background: "#e8f0f8", color: "#1e3a5f", fontFamily: "monospace" }}>
                                                    {p.key || "—"}
                                                </span>
                                            </td>
                                            <td>
                                                <div className="d-flex align-items-center gap-2">
                                                    <div
                                                        style={{
                                                            width: 30, height: 30, borderRadius: 6,
                                                            background: p.accentColor || "#1e3a5f",
                                                            color: "#e8f0f8",
                                                            display: "flex", alignItems: "center",
                                                            justifyContent: "center", fontSize: 14, flexShrink: 0,
                                                        }}
                                                    >
                                                        {p.icon || p.name?.slice(0, 2).toUpperCase()}
                                                    </div>
                                                    <div>
                                                        <div className="fw-medium">{p.name}</div>
                                                        {p.category && <div className="text-muted" style={{ fontSize: 11 }}>{p.category}</div>}
                                                    </div>
                                                </div>
                                            </td>
                                            <td><span className="badge bg-light text-dark border">{p.projectType || "—"}</span></td>
                                            <td>{getStatusBadge(p.status)}</td>
                                            <td>{getPriorityBadge(p.priority)}</td>
                                            <td><span className="badge bg-light text-dark border">{p.visibility || "—"}</span></td>
                                            <td>{p.customerName || "—"}</td>
                                            <td style={{ fontFamily: "monospace", fontSize: 12 }}>{formatDate(p.startDate)}</td>
                                            <td style={{ fontFamily: "monospace", fontSize: 12 }}>{formatDate(p.endDate)}</td>
                                            <td style={{ fontFamily: "monospace", fontSize: 12 }}>
                                                {p.estimatedBudget ? `${p.budgetCurrency || ""} ${Number(p.estimatedBudget).toLocaleString("en-IN")}` : "—"}
                                            </td>
                                            <td style={{ fontFamily: "monospace", fontSize: 12 }}>{formatDate(p.createdAt)}</td>
                                            <td>
                                                <div className="d-flex gap-1">
                                                    {p.status !== "Archived" && (
                                                        <button
                                                            className="btn btn-sm btn-outline-warning"
                                                            title="Archive"
                                                            onClick={() => openArchiveModal(p)}
                                                        >
                                                            <i className="bi bi-archive"></i>
                                                        </button>
                                                    )}
                                                    <button
                                                        className="btn btn-sm btn-outline-danger"
                                                        title="Delete"
                                                        onClick={() => openDeleteModal(p)}
                                                    >
                                                        <i className="bi bi-trash"></i>
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))
                                ) : (
                                    <tr>
                                        <td colSpan="13" className="text-muted text-center py-4">
                                            No projects found.
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>

                    {error && <p className="text-danger mt-3">{error}</p>}
                </div>
            </div>

            {/* ── Confirmation Modal ── */}
            <ConfirmModal
                isOpen={modal.isOpen}
                onClose={closeModal}
                onConfirm={handleModalConfirm}
                title={modalConfig.title}
                message={modalConfig.message}
                confirmLabel={modalConfig.confirmLabel}
                confirmVariant={modalConfig.confirmVariant}
                icon={modalConfig.icon}
            />
        </>
    );
};

export default AllProjects;